import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  Clock,
  Link2,
  ArrowRight,
} from "lucide-react";

/**
 * ConnectionsHealthStrip
 * ----------------------
 * Compact, one-line health view of the four launch platforms — read-only
 * summary powered entirely by the payload returned from GET /api/connections
 * (already fetched by the Dashboard). This component does NOT initiate
 * OAuth, change scopes, or mutate tokens; it only renders status +
 * navigates to the existing /settings/connections page for actions.
 *
 * Per-platform state precedence (mutually exclusive):
 *   1. needs_reauth === true                → "Reconnect" (amber)
 *   2. connected === true && !needs_reauth  → "Connected" (green)
 *   3. awaiting_app_review === true         → "Pending Meta review" (neutral)
 *   4. otherwise                            → "Not connected" (subtle)
 *
 * Meta's pending-review state is intentionally NEUTRAL, not red — it's
 * not a failure the owner needs to fix.
 */
const PLATFORM_ORDER = ["Facebook", "Instagram", "X", "LinkedIn"];

function platformKey(p) {
  return String(p || "").toLowerCase().replace(/[^a-z]/g, "");
}

function StatePill({ tone, label, Icon }) {
  const toneClass =
    tone === "ok"
      ? "text-[color:var(--success)] bg-[color:rgba(47,231,161,0.10)] border-[color:rgba(47,231,161,0.25)]"
      : tone === "warn"
        ? "text-[color:var(--accent-warm)] bg-[color:rgba(255,138,61,0.10)] border-[color:rgba(255,138,61,0.30)]"
        : tone === "info"
          ? "text-[color:var(--info)] bg-[color:rgba(77,183,255,0.08)] border-[color:rgba(77,183,255,0.25)]"
          : "text-[color:var(--text-3)] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]";
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border " +
        toneClass
      }
    >
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {label}
    </span>
  );
}

function Tile({ platform }) {
  const pk = platformKey(platform.platform);
  const needsReauth = !!platform.needs_reauth;
  const connected = !!platform.connected && !needsReauth;
  const awaitingReview = !!platform.awaiting_app_review;

  let state = "idle";
  let label = "Not connected";
  let Icon = Link2;
  if (needsReauth) {
    state = "warn";
    label = "Reconnect";
    Icon = AlertTriangle;
  } else if (connected) {
    state = "ok";
    label = "Connected";
    Icon = CheckCircle2;
  } else if (awaitingReview) {
    state = "info";
    label = "Pending Meta review";
    Icon = Clock;
  }

  return (
    <Link
      to="/settings/connections"
      data-testid={`dashboard-conn-tile-${pk}`}
      className="group flex items-center justify-between gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 hover:bg-[color:var(--surface-3)] transition"
      title={platform.status_message || label}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
          {platform.label || platform.platform}
        </div>
        <div className="mt-0.5">
          <StatePill tone={state} label={label} Icon={Icon} />
        </div>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-[color:var(--text-3)] opacity-0 group-hover:opacity-100 transition shrink-0" />
    </Link>
  );
}

export default function ConnectionsHealthStrip({ payload }) {
  const platforms = Array.isArray(payload?.platforms) ? payload.platforms : [];
  if (!platforms.length) return null;
  // Present the launch four in a stable order, ignoring "coming soon"
  // ones. The payload already tags coming_soon=true for TikTok etc. —
  // we filter them out of the compact strip so it stays scannable.
  const chosen = PLATFORM_ORDER
    .map((wanted) => platforms.find((p) => p.platform === wanted))
    .filter((p) => p && !p.coming_soon);
  if (!chosen.length) return null;
  return (
    <div
      className="rounded-[16px] glass p-4"
      data-testid="dashboard-connections-strip"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] flex items-center justify-center text-[color:var(--text-2)]">
            <Link2 className="h-3.5 w-3.5" />
          </div>
          <div className="font-display text-sm font-semibold text-[color:var(--text-1)]">
            Social connections
          </div>
        </div>
        <Link
          to="/settings/connections"
          className="text-[11px] text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1"
          data-testid="dashboard-connections-strip-manage"
        >
          Manage <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {chosen.map((p) => (
          <Tile key={p.platform} platform={p} />
        ))}
      </div>
    </div>
  );
}
